type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export interface InvalidField {
  element: FormField;
  message: string;
}

const isFormField = (element: Element): element is FormField =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLSelectElement ||
  element instanceof HTMLTextAreaElement;

const getFieldLabel = (element: FormField) => {
  const label = element.labels?.[0]?.textContent ?? element.getAttribute('aria-label') ?? '';
  return label.replace(/\*/g, '').trim() || element.name;
};

// Native constraints (required, maxLength, ...) cover empty mandatory fields, while
// aria-invalid covers fields Honeycomb marks as invalid via the `valid={false}` prop.
// Disabled and read-only inputs are skipped by native validation and are ignored here too.
export const findInvalidField = (form: HTMLFormElement | null | undefined): InvalidField | null => {
  if (!form) return null;

  for (const element of Array.from(form.elements)) {
    if (!isFormField(element) || element.disabled) continue;

    const label = getFieldLabel(element);

    if (!element.checkValidity()) {
      return {
        element,
        message: element.validity.valueMissing
          ? `"${label}" is required`
          : `"${label}" is not valid`,
      };
    }

    if (element.getAttribute('aria-invalid') === 'true') {
      return { element, message: `"${label}" is not valid` };
    }
  }

  return null;
};
